import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import notificationService from '../services/notificationService';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

// Création du contexte
const NotificationContext = createContext();

/**
 * Fournisseur du contexte de notification
 * @param {Object} props - Propriétés du composant
 * @returns {React.ReactNode} - Fournisseur du contexte
 */
export const NotificationProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [initialized, setInitialized] = useState(false);
    // Ajout pour le snackbar universel
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    
    // Flag pour éviter les appels multiples d'initialisation
    const initializingRef = useRef(false);

    // Charger les notifications non lues au démarrage
    useEffect(() => {
        if (isAuthenticated && !initialized && !initializingRef.current) {
            initializingRef.current = true;
            initializeNotifications();
        }

        return () => {
            if (initialized) {
                notificationService.cleanup();
            }
        };
    }, [isAuthenticated]); // Retirer 'initialized' pour éviter une boucle infinie

    /**
     * Initialiser le service de notification
     */
    const initializeNotifications = async () => {
        try {
            setLoading(true);
            setError(null);

            // Charger le compteur non-lu immédiatement via REST.
            // Le badge du header doit s'afficher même si Socket.io échoue.
            try {
                const result = await notificationService.getUnreadNotifications();
                setNotifications(result.notifications || []);
                setUnreadCount(result.count || 0);
            } catch (err) {
                console.warn('Could not load unread notifications on init:', err);
                setNotifications([]);
                setUnreadCount(0);
            }

            // Initialiser le service de notification avec les callbacks
            const success = await notificationService.initialize(
                handleNewNotification,
                handleNotificationRead,
                handleNotificationDeleted,
                handleAllNotificationsRead
            );

            if (!success) {
                console.warn('Notification socket initialization failed; keeping REST state');
            }

            setInitialized(true);
        } catch (err) {
            console.error('Error initializing notifications:', err);
            setError('Impossible de charger les notifications');
        } finally {
            setLoading(false);
            initializingRef.current = false;
        }
    };

    /**
     * Gérer une nouvelle notification
     * @param {Object} notification - Nouvelle notification
     */
    const handleNewNotification = useCallback((notification) => {
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);
    }, []);

    /**
     * Gérer une notification marquée comme lue
     * @param {string} notificationId - ID de la notification
     */
    const handleNotificationRead = useCallback((notificationId) => {
        setNotifications(prev =>
            prev.map(notification =>
                notification.id === notificationId
                    ? { ...notification, isRead: true }
                    : notification
            )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
    }, []);

    /**
     * Gérer une notification supprimée
     * @param {string} notificationId - ID de la notification
     */
    const handleNotificationDeleted = useCallback((notificationId) => {
        setNotifications(prev => {
            const notification = prev.find(n => n.id === notificationId);
            const newNotifications = prev.filter(n => n.id !== notificationId);

            // Si la notification supprimée n'était pas lue, décrémenter le compteur
            if (notification && !notification.isRead) {
                setUnreadCount(count => Math.max(0, count - 1));
            }

            return newNotifications;
        });
    }, []);

    /**
     * Gérer toutes les notifications marquées comme lues
     */
    const handleAllNotificationsRead = useCallback(() => {
        setNotifications(prev =>
            prev.map(notification => ({ ...notification, isRead: true }))
        );
        setUnreadCount(0);
    }, []);

    /**
     * Marquer une notification comme lue
     * @param {string} notificationId - ID de la notification
     */
    const markAsRead = async (notificationId) => {
        try {
            await notificationService.markAsRead(notificationId);
            // Le handleNotificationRead sera appelé via Socket.io
        } catch (err) {
            console.error('Error marking notification as read:', err);
            setError('Impossible de marquer la notification comme lue');
        }
    };

    /**
     * Marquer toutes les notifications comme lues
     */
    const markAllAsRead = async () => {
        try {
            await notificationService.markAllAsRead();
            // Le handleAllNotificationsRead sera appelé via Socket.io
        } catch (err) {
            console.error('Error marking all notifications as read:', err);
            setError('Impossible de marquer toutes les notifications comme lues');
        }
    };

    /**
     * Supprimer une notification
     * @param {string} notificationId - ID de la notification
     */
    const deleteNotification = async (notificationId) => {
        try {
            await notificationService.deleteNotification(notificationId);
            // Le handleNotificationDeleted sera appelé via Socket.io
        } catch (err) {
            console.error('Error deleting notification:', err);
            setError('Impossible de supprimer la notification');
        }
    };

    /**
     * Supprimer toutes les notifications
     */
    const deleteAllNotifications = async () => {
        try {
            await notificationService.deleteAllNotifications();
            setNotifications([]);
            setUnreadCount(0);
        } catch (err) {
            console.error('Error deleting all notifications:', err);
            setError('Impossible de supprimer toutes les notifications');
        }
    };

    /**
     * Charger plus de notifications (pagination)
     * @param {Object} options - Options de pagination et de filtrage
     */
    const loadMoreNotifications = async (options = {}) => {
        try {
            setLoading(true);
            setError(null);

            const result = await notificationService.getNotifications(options);

            // Le endpoint paginé renvoie le compteur global non-lu.
            if (typeof result?.unreadCount === 'number') {
                setUnreadCount(result.unreadCount);
            }

            // Si c'est la première page, remplacer les notifications
            // Sinon, ajouter les nouvelles notifications à la liste existante
            if (options.page === 1) {
                setNotifications(result.notifications || []);
            } else {
                setNotifications(prev => [...prev, ...(result.notifications || [])]);
            }

            return result;
        } catch (err) {
            console.error('Error loading more notifications:', err);
            setError('Impossible de charger plus de notifications');
            return null;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Envoyer une notification de test (pour le développement)
     * @param {Object} notification - Données de la notification
     */
    const sendTestNotification = async (notification) => {
        try {
            await notificationService.sendTestNotification(notification);
        } catch (err) {
            console.error('Error sending test notification:', err);
            setError('Impossible d\'envoyer la notification de test');
        }
    };

    // Fonction pour afficher une notification (snackbar)
    const showNotification = (message, severity = 'info') => {
        setSnackbar({ open: true, message, severity });
    };

    // Valeur du contexte
    const value = {
        notifications,
        unreadCount,
        loading,
        error,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        deleteAllNotifications,
        loadMoreNotifications,
        sendTestNotification,
        showNotification // <-- ici
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert
                    onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                    elevation={6}
                    variant="filled"
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </NotificationContext.Provider>
    );
};

/**
 * Hook pour utiliser le contexte de notification
 * @returns {Object} - Contexte de notification
 */
export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error('useNotifications doit être utilisé à l\'intérieur d\'un NotificationProvider');
    }
    return context;
};

export default NotificationContext;